import type { ThemeConfig } from 'antd';

/**
 * Rescue Foodies Consumer App Theme Configuration
 * 
 * Brand Colors:
 * - Primary (Orange): #FF6B35 - CTAs, primary actions
 * - Success (Green): #2D5F3F - Success states, sustainability
 * 
 * Design System: docs/front-end-spec.md
 */
export const consumerTheme: ThemeConfig = {
  token: {
    // === BRAND COLORS ===
    colorPrimary: '#FF6B35',        // Orange for CTAs (Reserve Bag, etc.)
    colorSuccess: '#2D5F3F',        // Green for success states
    colorError: '#F44336',          // Danger red
    colorWarning: '#FFA726',        // Warning orange
    colorInfo: '#2196F3',           // Info blue
    
    // === TYPOGRAPHY ===
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    fontSize: 16,                   // Base font size
    fontSizeHeading1: 36,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontWeightStrong: 600,
    lineHeight: 1.5,
    lineHeightHeading1: 1.25,
    lineHeightHeading2: 1.25,
    
    // === SPACING & SIZING ===
    borderRadius: 12,               // Default border radius
    borderRadiusLG: 16,             // Large cards
    borderRadiusSM: 8,              // Small elements
    controlHeight: 48,              // Button/input height (WCAG AA compliant)
    controlHeightLG: 56,            // Large buttons
    controlHeightSM: 40,            // Small buttons
    
    // === LAYOUT ===
    padding: 16,
    paddingLG: 24,
    paddingXL: 32,
    margin: 16,
    marginLG: 24,
    marginXL: 32,
    
    // === BORDERS ===
    lineWidth: 2,
    colorBorder: '#E0E0E0',         // Gray-300
    colorBorderSecondary: '#EEEEEE',
    
    // === BACKGROUNDS ===
    colorBgContainer: '#FFFFFF',    // White for cards
    colorBgLayout: '#FAFAFA',       // Light gray for page background
    colorBgElevated: '#FFFFFF',
    colorFillQuaternary: '#F5F5F5',
    
    // === TEXT COLORS ===
    colorText: '#212121',           // Primary text (gray-900)
    colorTextSecondary: '#616161',  // Secondary text (gray-700)
    colorTextTertiary: '#9E9E9E',   // Tertiary text (gray-500)
    colorTextQuaternary: '#BDBDBD', // Disabled text (gray-400)
    
    // === ELEVATION (SHADOWS) ===
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 8px rgba(0, 0, 0, 0.04)',
    boxShadowSecondary: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
    
    // === MOTION ===
    motionDurationMid: '0.25s',
    motionDurationSlow: '0.35s',
    motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  
  components: {
    // === BUTTONS ===
    Button: {
      controlHeight: 48,
      controlHeightLG: 56,
      borderRadius: 12,
      fontWeight: 600,
      primaryShadow: '0 4px 16px rgba(255, 107, 53, 0.24)', // Orange glow
    },
    
    // === CARDS ===
    Card: {
      borderRadius: 16,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 8px rgba(0, 0, 0, 0.04)',
      paddingLG: 24,
    },
    
    // === TAGS/BADGES ===
    Tag: {
      borderRadiusSM: 20, // Pill shape for category badges
      fontSize: 12,
      fontSizeSM: 11,
    },
    
    // === INPUTS ===
    Input: {
      controlHeight: 48,
      borderRadius: 12,
      paddingBlock: 12,
      paddingInline: 16,
      activeBorderColor: '#FF6B35',
      hoverBorderColor: '#FF6B35',
    },
    
    // === SELECT ===
    Select: {
      controlHeight: 48,
      borderRadius: 12,
    },
    
    // === LAYOUT ===
    Layout: {
      headerBg: '#FFFFFF',
      headerHeight: 72,
      footerBg: '#FAFAFA',
      bodyBg: '#FAFAFA',
    },
    
    // === MENU ===
    Menu: {
      itemBorderRadius: 8,
      itemHoverBg: '#FFF3E0', // Light orange on hover
      itemSelectedBg: '#FFE0B2',
      itemSelectedColor: '#FF6B35',
    },
  },
};
